import {ModalInstance, ModalInstanceOld} from '@/components/L/Modal/ModalTypes';
import {useObserver} from '@/composables/useObserver';
import {defineStore} from 'pinia';
import {Ref, ref} from 'vue';

const STORE_ID = 'modal-store' as const
const getModalObserverKey = (modalUuid: string): string => 'modal-' + modalUuid;

const isModalInstance = (
  modalInstance: ModalInstance | ModalInstanceOld,
): modalInstance is ModalInstance => 'props' in modalInstance
const isModalInstanceOld = (
  modalInstance: ModalInstance | ModalInstanceOld,
): modalInstance is ModalInstanceOld => 'modalData' in modalInstance
const hasModalDataName = (value: unknown): value is { modalData: { name: string } } => {
  if (!value || typeof value !== 'object') return false;
  const maybeModalData = (value as Record<string, unknown>).modalData;
  if (!maybeModalData || typeof maybeModalData !== 'object') return false;
  return 'name' in (maybeModalData as Record<string, unknown>);
}
const hasModalName = (value: unknown): value is { name: string } => {
  if (!value || typeof value !== 'object') return false;
  return 'name' in (value as Record<string, unknown>);
}
export const useModalStore = defineStore(STORE_ID, () => {
  const modals: Ref<Array<ModalInstance | ModalInstanceOld>> = ref([])

  const addModal = (newModal: ModalInstance | ModalInstanceOld, closeModal?: Function) => {
    modals.value.push(newModal);
    if (isModalInstance(newModal)) {
      if (!closeModal) throw new Error('Modal of class ModalInstance needs closeModalFunction to work properly!');

      const { subscribe, } = useObserver<ModalInstance>(getModalObserverKey(newModal.uuid));
      subscribe((modalPayload) => {
        closeModal(modalPayload);
      })
    }
  }

  const closeModal = <T>(modalInstance: ModalInstance | ModalInstanceOld, payload?: T) => {
    const modalIndex = modals.value.findIndex(modal => modal.uuid === modalInstance.uuid);

    if (modalIndex === -1) {
      throw new Error(`Modal with uuid ${modalInstance.uuid} not found`);
    }

    removeModalInstance(modalIndex);
    if (isModalInstance(modalInstance)) {
      const { updateSubject, } = useObserver<T>(getModalObserverKey(modalInstance.uuid));
      updateSubject(payload);
    }
  };

  const removeModalInstance = (index: number) => {
    modals.value.splice(index, 1);
  };

  const closeAllModals = () => {
    modals.value.slice().forEach(modal => {
      closeModal(modal);
    });
  }

  const closeIframeModal = (modalName: string, data?: any) => {
    const modal = modals.value.find(modal => {
      if (isModalInstance(modal)) {
        return hasModalDataName(modal.props) && modal.props.modalData.name === modalName;
      }
      return isModalInstanceOld(modal) && hasModalName(modal.modalData) && modal.modalData.name === modalName;
    });
    if (modal) {
      closeModal(modal, data);
    }
  }

  return {
    modals,
    addModal,
    closeModal,
    closeAllModals,
    closeIframeModal,
  }
})
